import { supabase } from "./supabase";

export type TypeProduit = "prestation" | "marchandises" | "compte_specifique";

export interface Produit {
  id: string;
  dossier_id: string;
  nom: string;
  description?: string | null;
  reference?: string | null;
  prix_ht: number;
  taux_tva: number;
  unite: string;
  devise: string;
  type_produit: TypeProduit;
  compte_comptable?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** Recherche de produits par nom (débrayable côté appelant) */
export async function searchProduits(
  dossierId: string,
  query: string,
  limit = 15
): Promise<Produit[]> {
  const q = String(query).trim();
  if (!dossierId) return [];
  let builder = supabase
    .from("produits")
    .select("id, dossier_id, nom, description, reference, prix_ht, taux_tva, unite, devise, type_produit, compte_comptable")
    .eq("dossier_id", dossierId)
    .order("nom");
  if (q.length > 0) {
    builder = builder.ilike("nom", `%${q}%`);
  }
  const { data, error } = await builder.limit(limit);
  if (error) throw error;
  return (data || []) as Produit[];
}

/** Liste tous les produits du dossier */
export async function fetchProduits(dossierId: string): Promise<Produit[]> {
  if (!dossierId) return [];
  const { data, error } = await supabase
    .from("produits")
    .select("id, dossier_id, nom, description, reference, prix_ht, taux_tva, unite, devise, type_produit, compte_comptable, created_at, updated_at")
    .eq("dossier_id", dossierId)
    .order("nom");
  if (error) throw error;
  return (data || []) as Produit[];
}

/** Création d'un produit */
export async function createProduit(
  dossierId: string,
  input: {
    nom: string;
    description?: string | null;
    reference?: string | null;
    prix_ht: number;
    taux_tva: number;
    unite: string;
    devise?: string;
    type_produit?: TypeProduit;
    compte_comptable?: string | null;
  }
): Promise<Produit> {
  const { data, error } = await supabase
    .from("produits")
    .insert({
      dossier_id: dossierId,
      nom: input.nom.trim(),
      description: input.description?.trim() || null,
      reference: input.reference?.trim() || null,
      prix_ht: Number(input.prix_ht) || 0,
      taux_tva: Number(input.taux_tva) ?? 18,
      unite: input.unite?.trim() || "Unité(s)",
      devise: input.devise?.trim() || "FCFA",
      type_produit: input.type_produit || "prestation",
      compte_comptable: input.compte_comptable?.trim() || null,
    })
    .select()
    .single();
  if (error) throw error;
  return data as Produit;
}

/** Mise à jour d'un produit */
export async function updateProduit(
  produitId: string,
  input: {
    nom: string;
    description?: string | null;
    reference?: string | null;
    prix_ht: number;
    taux_tva: number;
    unite: string;
    devise?: string;
    type_produit?: TypeProduit;
    compte_comptable?: string | null;
  }
): Promise<Produit> {
  const { data, error } = await supabase
    .from("produits")
    .update({
      nom: input.nom.trim(),
      description: input.description?.trim() || null,
      reference: input.reference?.trim() || null,
      prix_ht: Number(input.prix_ht) || 0,
      taux_tva: Number(input.taux_tva) ?? 18,
      unite: input.unite?.trim() || "Unité(s)",
      devise: input.devise?.trim() || "FCFA",
      type_produit: input.type_produit || "prestation",
      compte_comptable: input.compte_comptable?.trim() || null,
    })
    .eq("id", produitId)
    .select()
    .single();
  if (error) throw error;
  return data as Produit;
}

/** Suppression d'un produit */
export async function deleteProduit(produitId: string): Promise<void> {
  const { error } = await supabase.from("produits").delete().eq("id", produitId);
  if (error) throw error;
}
